const FONT_METRICS = {
  0: 0.49, 1: 0.41, 2: 0.449, 3: 0.461, 4: 0.487, 5: 0.456, 6: 0.486, 7: 0.452, 8: 0.488, 9: 0.489,
  a: 0.498, b: 0.489, c: 0.527, d: 0.529, e: 0.444, f: 0.431, g: 0.536, h: 0.544, i: 0.231, j: 0.235,
  k: 0.511, l: 0.403, m: 0.649, n: 0.545, o: 0.532, p: 0.465, q: 0.532, r: 0.504, s: 0.462, t: 0.448,
  u: 0.532, v: 0.508, w: 0.72, x: 0.485, y: 0.48, z: 0.455,
  A: 0.498, B: 0.489, C: 0.527, D: 0.529, E: 0.444, F: 0.431, G: 0.536, H: 0.544, I: 0.231, J: 0.235,
  K: 0.511, L: 0.403, M: 0.649, N: 0.545, O: 0.532, P: 0.465, Q: 0.532, R: 0.504, S: 0.462, T: 0.448,
  U: 0.532, V: 0.508, W: 0.72, X: 0.485, Y: 0.48, Z: 0.455,
  ' ': 0.155, '&': 0.542, ':': 0.239, '.': 0.246, '™': 0.64,
};

const GLUE_LEFT = [/^\d+$/];
const GLUE_RIGHT = [
  '&', 'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'no', 'not', 'of', 'or', 'per', 'the', 'to',
  /^\d+$/,
];

const FALLBACK_METRIC = Math.max(...Object.values(FONT_METRICS));

const KEEP_UPPER = new Set([
  'VIP', 'TV', 'FS', 'RNG', 'BJ', 'MB', 'UK', 'US', 'EU', 'PT', 'BR', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'NO', 'DK', 'PL', 'TR', 'RU', 'CN', 'JP', 'KR', 'VN',
]);

function titleToken(token) {
  if (!token) return token;
  if (/^\d+[A-Za-z]?$/.test(token)) return token;
  const upper = token.toUpperCase();
  if (KEEP_UPPER.has(upper)) return upper;
  return upper.charAt(0) + upper.slice(1).toLowerCase();
}

/** Clean slug junk and HTML artifacts in game titles. */
export function formatGameDisplayName(name) {
  let raw = String(name ?? '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/[\uFF3F\u2017\u02CD]/g, '_')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
    .replace(/\$+([Ee])rie[sS]\b/g, ' Series')
    .replace(/\$+([Ee])rie[sS]/g, ' Series')
    .replace(/\$+\s*Series\b/g, ' Series')
    .replace(/\s*\|{4}\s*$/g, ' IV')
    .replace(/\s*\|{3}\s*$/g, ' III')
    .replace(/\s*\|{2}\s*$/g, ' II')
    .replace(/\s*\|\s*$/g, ' I')
    .replace(/\s*\|\|+\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[\s|_.-]+|[\s|_.-]+$/g, '')
    .trim();
  if (!raw) return '';
  const known = ({
    'd and w': 'Drops & Wins',
    'd & w': 'Drops & Wins',
  })[raw.toLowerCase()];
  if (known) return known;
  let expanded = raw
    .replace(/\bBlackjackx\b/gi, 'Blackjack X')
    .replace(/\bMegaroulette([0-9]+)\b/gi, 'Mega Roulette $1')
    .replace(/\bPunto\s*Banco([A-Z])/g, 'Punto Banco $1')
    .replace(/([A-Za-z])([0-9]{3,})\b/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  const isSlug = expanded.includes('_') || /^[A-Z0-9]+(?:-[A-Z0-9]+)+$/.test(expanded);
  if (!isSlug && expanded === raw) return raw;
  if (!isSlug && expanded !== raw) {
    return expanded.split(' ').map(titleToken).join(' ');
  }
  raw = expanded;
  if (!isSlug) return raw;
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(titleToken)
    .join(' ');
}

export function formatProviderDisplayName(name) {
  return String(name || '').toLocaleUpperCase('en-US');
}

function fromToByStep(start, end, step) {
  const out = [];
  for (let value = start; value <= end + 1e-12; value = Number((value + step).toFixed(10))) {
    out.push(Number(value.toFixed(10)));
  }
  return out;
}

function tokenMatches(token, rules) {
  const lower = token.toLowerCase();
  for (const rule of rules) {
    if (typeof rule === 'string') {
      if (lower === rule.toLowerCase()) return true;
    } else if (rule instanceof RegExp && rule.test(token)) {
      return true;
    }
  }
  return false;
}

function glueTokens(words, glueLeft, glueRight) {
  const tokens = [...words];
  for (let i = 0; i < tokens.length - 1; i += 1) {
    if (tokenMatches(tokens[i], glueRight)) {
      tokens[i] = `${tokens[i]} ${tokens[i + 1]}`;
      tokens.splice(i + 1, 1);
    }
  }
  for (let i = 1; i < tokens.length; i += 1) {
    if (tokenMatches(tokens[i], glueLeft)) {
      tokens[i - 1] = `${tokens[i - 1]} ${tokens[i]}`;
      tokens.splice(i, 1);
      i -= 1;
    }
  }
  return tokens;
}

function measureTextWidth(text, fontSize) {
  let width = 0;
  for (const char of text) {
    width += (FONT_METRICS[char] ?? FALLBACK_METRIC) * fontSize;
  }
  return width;
}

export function prepareGameTitle(name, maxWidth, { shrink = 0, compact = false } = {}) {
  const raw = formatGameDisplayName(name).trim();
  const width = Math.max(1, Number(maxWidth) || 116);
  const isCompact = compact || width <= 118;
  const linesCfg = isCompact
    ? [
      { sizes: fromToByStep(14 - shrink, 17 - shrink, 0.1), maxWidth: width },
      { sizes: fromToByStep(10 - shrink, 13 - shrink, 0.1), maxWidth: width },
      { sizes: fromToByStep(12 - shrink, 19 - shrink, 0.1), maxWidth: width },
    ]
    : [
      { sizes: fromToByStep(18 - shrink, 21 - shrink, 0.1), maxWidth: width },
      { sizes: fromToByStep(14 - shrink, 18 - shrink, 0.1), maxWidth: width },
    ];

  const state = {
    info: linesCfg.map((line) => ({ fontIndex: line.sizes.length - 1, width: 0 })),
    lines: linesCfg.map((line) => ({ fontSize: line.sizes[line.sizes.length - 1], value: '' })),
  };

  if (!raw) return state.lines;

  const tokens = raw.includes('\n')
    ? raw.split('\n')
    : glueTokens(
      raw.split(' ').map((part) => part.trim()).filter(Boolean),
      GLUE_LEFT,
      GLUE_RIGHT,
    );

  for (;;) {
    let lineIndex = 0;
    for (const token of tokens) {
      const canWrap = lineIndex < linesCfg.length - 1;
      const current = state.lines[lineIndex];
      const candidate = current.value ? `${current.value} ${token}` : token;
      const candidateWidth = measureTextWidth(candidate, current.fontSize);
      if (current.value && candidateWidth > linesCfg[lineIndex].maxWidth && canWrap) {
        const next = state.lines[lineIndex + 1];
        next.value = token;
        state.info[lineIndex + 1].width = measureTextWidth(token, next.fontSize);
        lineIndex += 1;
      } else {
        current.value = candidate;
        state.info[lineIndex].width = candidateWidth;
      }
    }

    let shrunk = false;
    for (let i = linesCfg.length - 1; i >= 0; i -= 1) {
      const info = state.info[i];
      if (info.width <= linesCfg[i].maxWidth) continue;
      if (info.fontIndex <= 0) continue;
      info.fontIndex -= 1;
      state.lines[i].fontSize = linesCfg[i].sizes[info.fontIndex];
      shrunk = true;
    }
    if (!shrunk) break;
    state.info.forEach((info) => { info.width = 0; });
    state.lines.forEach((line) => { line.value = ''; });
  }

  return state.lines;
}

export const GAME_CARD_NAME_PRIMARY_COMPACT = 20.9;
export const GAME_CARD_NAME_SECONDARY_COMPACT = 17.9;
export const GAME_CARD_NAME_PRIMARY_PAGE = 18.9;
export const GAME_CARD_NAME_SECONDARY_PAGE = 16;
export const GAME_CARD_PROVIDER_SIZE = 12;

export function splitGameName(name, maxWidth = 116) {
  const lines = prepareGameTitle(name, maxWidth, { compact: true });
  const primary = lines[0]?.value || '';
  const rest = lines.slice(1).map((line) => line?.value).filter(Boolean).join(' ');
  return {
    primary,
    secondary: rest,
    primarySize: lines[0]?.fontSize || GAME_CARD_NAME_PRIMARY_COMPACT,
    secondarySize: lines[1]?.fontSize || GAME_CARD_NAME_SECONDARY_COMPACT,
  };
}


export function gameCardNameSizes(variant = 'page') {
  if (variant === 'compact') {
    return {
      primary: GAME_CARD_NAME_PRIMARY_COMPACT,
      secondary: GAME_CARD_NAME_SECONDARY_COMPACT,
      provider: GAME_CARD_PROVIDER_SIZE,
    };
  }
  return {
    primary: GAME_CARD_NAME_PRIMARY_PAGE,
    secondary: GAME_CARD_NAME_SECONDARY_PAGE,
    provider: GAME_CARD_PROVIDER_SIZE,
  };
}

export function gameCardDimensions(cardWidth) {
  const width = Math.max(1, cardWidth);
  const ratio = 19 / 14;
  return {
    width,
    height: Math.round(width * ratio),
    labelHeight: Math.round(width * (width <= 118 ? 52 / 156 : 55 / 200)),
  };
}

export function gameCardStyleVars(cardWidth, variant = 'page') {
  const dims = gameCardDimensions(cardWidth);
  const fonts = gameCardNameSizes(variant);
  return {
    '--game-card-w': `${dims.width}px`,
    '--game-card-h': `${dims.height}px`,
    '--game-card-label-h': `${dims.labelHeight}px`,
    '--game-card-name-primary': `${fonts.primary}px`,
    '--game-card-name-secondary': `${fonts.secondary}px`,
    '--game-card-provider-size': `${fonts.provider}px`,
    '--game-card-variant': variant,
  };
}
